import { useState } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { FormField, Input, Select } from '../../../components/ui/FormField.js';
import { Switch } from '../../../components/ui/Switch.js';
import { LastChanged } from '../parts/LastChanged.js';
import { PendingChanges, SettingCard, StatusChip } from '../parts/parts.js';
import { CHANNEL_NAMES, channelName, formatMyt } from '../settingsFormat.js';
import type { ChannelPatch, Fee } from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function SwitchRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 text-sm text-ink">
      <Switch checked={checked} onChange={() => onChange(!checked)} ariaLabel={label} />
      <span>{label}</span>
    </div>
  );
}

const ringgit = (minor: number) => (minor / 100).toFixed(2);
const toMinor = (v: string) => Math.round(Number(v) * 100);

/** 097: the platform fee. */
export function PlatformFeeCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.platform_fee;
  const fee = draft.value('platform_fee');
  if (!saved || !fee) return null;
  const set = (patch: Partial<Fee>) => draft.set('platform_fee', { ...fee, ...patch });
  return (
    <SettingCard
      testId="platform-fee-card"
      title="Platform fee"
      dirty={draft.isDirty('platform_fee')}
      refusal={refusal('platform_fee')}
      chip={
        <StatusChip
          testId="status-platform_fee"
          tone={saved.enabled ? 'on' : 'off'}
          since={latest?.platform_fee?.state_since}
        >
          {saved.enabled ? `ON · RM ${ringgit(saved.amount_minor)}` : 'OFF'}
        </StatusChip>
      }
      description={<p>Added to every priced quote. Takes effect on the next quote.</p>}
      footer={<LastChanged setting="platform_fee" latest={latest} />}
    >
      <SwitchRow
        label="Platform fee enabled"
        checked={fee.enabled}
        onChange={(v) => set({ enabled: v })}
      />
      <FormField label="Amount (RM)" name="platform_fee_amount">
        <Input
          id="platform_fee_amount"
          type="number"
          min={0}
          step="0.01"
          value={ringgit(fee.amount_minor)}
          onChange={(e) => set({ amount_minor: toMinor(e.target.value) })}
        />
      </FormField>
      <Check
        label="Taxable (counts toward the SST base)"
        checked={fee.taxable}
        onChange={(v) => set({ taxable: v })}
      />
      <Check label="Refundable" checked={fee.refundable} onChange={(v) => set({ refundable: v })} />
      <PendingChanges setting="platform_fee" changes={draft.changesOf('platform_fee')} />
    </SettingCard>
  );
}

/** 097: SST. */
export function SstCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.sst;
  const sst = draft.value('sst');
  if (!saved || !sst) return null;
  return (
    <SettingCard
      testId="sst-card"
      title="SST"
      dirty={draft.isDirty('sst')}
      refusal={refusal('sst')}
      chip={
        <StatusChip
          testId="status-sst"
          tone={saved.enabled ? 'on' : 'off'}
          since={latest?.sst?.state_since}
        >
          {saved.enabled ? `ON · ${saved.rate_bps / 100}%` : 'OFF'}
        </StatusChip>
      }
      description={<p>Applied over the rental plus every taxable enabled charge.</p>}
      footer={<LastChanged setting="sst" latest={latest} />}
    >
      <SwitchRow
        label="SST enabled"
        checked={sst.enabled}
        onChange={(v) => draft.set('sst', { ...sst, enabled: v })}
      />
      <FormField label="Rate (%)" name="sst_rate">
        <Input
          id="sst_rate"
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={(sst.rate_bps / 100).toFixed(2)}
          onChange={(e) =>
            draft.set('sst', { ...sst, rate_bps: Math.round(Number(e.target.value) * 100) })
          }
        />
      </FormField>
      <PendingChanges setting="sst" changes={draft.changesOf('sst')} />
    </SettingCard>
  );
}

const NEW_CHANNEL: ChannelPatch = {
  enabled: false,
  fee: { enabled: false, amount_minor: 0, taxable: true, refundable: false },
};

/** 459: the payment channels, each with its own fee. A channel added here starts switched off. */
export function PaymentChannelsCard({ draft, config, latest, refusal }: CardProps) {
  const saved = draft.saved?.channel_configs;
  const channels = draft.value('channel_configs');
  const [toAdd, setToAdd] = useState('');
  if (!saved || !channels) return null;
  const savedIds = Object.keys(saved);
  const onCount = savedIds.filter((id) => saved[id]?.enabled).length;
  const available = Object.keys(CHANNEL_NAMES).filter((id) => !(id in channels));
  const addId = available.includes(toAdd) ? toAdd : (available[0] ?? '');
  const setChannel = (id: string, next: ChannelPatch) =>
    draft.set('channel_configs', { ...channels, [id]: next });

  return (
    <SettingCard
      testId="payment-channels-card"
      title="Payment channels"
      dirty={draft.isDirty('channel_configs')}
      refusal={refusal('channel_configs')}
      chip={
        <StatusChip testId="status-payment_channels" tone={onCount === 0 ? 'off' : 'on'}>
          {`${onCount} of ${savedIds.length} on`}
        </StatusChip>
      }
      description={
        <p>
          Which payment methods customers may choose at checkout, and each one's own fee. A channel
          switched off disappears from the picker at once; a channel not listed is never offered.
        </p>
      }
      footer={<LastChanged setting="payment_channels" latest={latest} />}
    >
      <div className="flex flex-col gap-5">
        {Object.entries(channels)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([id, channel]) => {
            const stored = config.channel_configs?.[id];
            const fee = channel.fee;
            const setFee = (patch: Partial<Fee>) =>
              setChannel(id, { ...channel, fee: { ...fee, ...patch } });
            return (
              <div
                key={id}
                data-testid={`channel-${id}`}
                className="flex flex-col gap-3 border-t border-hairline-soft pt-4 first:border-t-0 first:pt-0"
              >
                <h3 className="text-sm font-semibold text-ink">{channelName(id)}</h3>
                <SwitchRow
                  label={`${channelName(id)} enabled`}
                  checked={channel.enabled}
                  onChange={(v) => setChannel(id, { ...channel, enabled: v })}
                />
                {stored && !stored.enabled && stored.disabled_by === 'system' && (
                  <p
                    data-testid={`channel-${id}-auto-disabled`}
                    className="text-sm text-semantic-warning-text"
                  >
                    Switched off by the system
                    {stored.disabled_at ? ` ${formatMyt(stored.disabled_at)}` : ''} after repeated
                    failures. Check the issue is fixed before switching it on.
                  </p>
                )}
                {stored && !stored.enabled && stored.disabled_by === 'staff' && (
                  <p className="text-sm text-steel">Switched off by staff.</p>
                )}
                {!stored && <p className="text-sm text-steel">New: saved with this tab.</p>}
                <FormField label="Amount (RM)" name={`channel_${id}_amount`}>
                  <Input
                    id={`channel_${id}_amount`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={ringgit(fee.amount_minor)}
                    onChange={(e) => setFee({ amount_minor: toMinor(e.target.value) })}
                  />
                </FormField>
                <Check
                  label="Fee enabled"
                  checked={fee.enabled}
                  onChange={(v) => setFee({ enabled: v })}
                />
                <Check
                  label="Taxable (counts toward the SST base)"
                  checked={fee.taxable}
                  onChange={(v) => setFee({ taxable: v })}
                />
                <Check
                  label="Refundable"
                  checked={fee.refundable}
                  onChange={(v) => setFee({ refundable: v })}
                />
              </div>
            );
          })}
      </div>
      {available.length > 0 && (
        <div className="flex items-end gap-3 border-t border-hairline-soft pt-4">
          <FormField label="Add payment channel" name="channel_add">
            <Select
              id="channel_add"
              data-testid="channel-add-select"
              value={addId}
              onChange={(e) => setToAdd(e.target.value)}
            >
              {available.map((id) => (
                <option key={id} value={id}>
                  {CHANNEL_NAMES[id]}
                </option>
              ))}
            </Select>
          </FormField>
          <Button
            type="button"
            variant="secondary"
            data-testid="channel-add-button"
            onClick={() => addId && setChannel(addId, NEW_CHANNEL)}
          >
            Add channel
          </Button>
        </div>
      )}
      <PendingChanges setting="payment_channels" changes={draft.changesOf('channel_configs')} />
    </SettingCard>
  );
}
